import React, { useEffect, useState } from 'react';
import type { PDFDocumentLoadingTask, PDFDocumentProxy } from 'pdfjs-dist';
import { FileText, Loader2, Share2, ZoomIn, ZoomOut } from 'lucide-react';
import { Button } from './Button';
import { blobFuerUrl } from '../services/nativeShims';

// ─── PDF-Vorschau in der Android-App ─────────────────────────────────────────

export interface NativePdfSeitenProps {
    /** blob:-, data:- oder http(s)-Adresse. Wird nur beim Einhängen gelesen — neuer Stand = neuer `key`. */
    pdfUrl: string;
    /**
     * Gibt das VOLLSTÄNDIGE PDF aus (Teilen-Dialog, `dateiAusgeben`). Knopf im
     * Ersatzinhalt, wenn sich keine Seite darstellen ließ, und unter einer
     * gekürzten Vorschau (`maxSeiten`). Ohne: kein Knopf.
     */
    onTeilen?: () => void;
    /** Sperrt den Knopf, solange der Teilen-Dialog aufgeht (zweites Tippen liefe ins „Teilen läuft schon"). */
    teilenLaeuft?: boolean;
    /** Höchstens so viele Seiten rendern (Vorgabe: alle). Der Rest steht im PDF selbst. */
    maxSeiten?: number;
    /**
     * Nur zusammen mit `maxSeiten`: unter der gekürzten Vorschau steht
     * „Weitere Seiten anzeigen" und rendert die nächsten so vielen Seiten
     * dazu — die ersten bleiben stehen und werden nicht neu aufgebaut.
     * Ohne: wie bisher nur der Kürzungshinweis.
     */
    nachladenSchritt?: number;
    /** Beschriftung des Ausgabe-Knopfs (Vorgabe „PDF teilen / speichern"). */
    teilenText?: string;
}

/** Zielbreite je Seite: ~150 dpi bei A4, wie beim Briefpapier (stationery.ts). */
const SEITENBREITE_PX = 1240;

/** Wie viele Seiten gerendert werden: alle, oder höchstens `maxSeiten` (ganze Zahl ≥ 1). */
function seitenGrenze(gesamt: number, maxSeiten?: number): number {
    if (maxSeiten === undefined || !Number.isFinite(maxSeiten) || maxSeiten < 1) return gesamt;
    return Math.min(gesamt, Math.floor(maxSeiten));
}

/** pdf.js-Auftrag bzw. -Dokument freigeben; ein Fehler dabei ändert nichts mehr. */
function zerstoere(ziel: { destroy(): Promise<void> } | null): void {
    if (!ziel) return;
    ziel.destroy().catch((e: unknown) => console.warn('[NativePdfSeiten] pdf.js ließ sich nicht sauber beenden', e));
}

/** Eine pdf.js-Seite als JPEG-Objekt-URL; das Canvas wird gleich wieder freigegeben. */
async function seiteAlsBild(pdf: PDFDocumentProxy, nr: number): Promise<string> {
    const seite = await pdf.getPage(nr);
    try {
        const basis = seite.getViewport({ scale: 1 });
        const viewport = seite.getViewport({ scale: SEITENBREITE_PX / basis.width });
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(viewport.width);
        canvas.height = Math.round(viewport.height);
        const ctx = canvas.getContext('2d');
        if (!ctx) throw new Error('Canvas-Kontext nicht verfügbar.');
        // Weißer Grund, sonst werden transparente Flächen im JPEG schwarz.
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        await seite.render({ canvasContext: ctx, viewport }).promise;
        const bild = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.85));
        // Speicher sofort freigeben — ältere Geräte halten sonst je Seite ~6 MB.
        canvas.width = 0;
        canvas.height = 0;
        if (!bild) throw new Error('Seite ließ sich nicht als Bild ablegen.');
        return URL.createObjectURL(bild);
    } finally {
        seite.cleanup();
    }
}

/**
 * In der Android-App (docs/android-app-vertrag.md E11/E14): Die WebView hat
 * keinen PDF-Betrachter. Ein `<iframe>`/`<object>` mit einem PDF bleibt dort
 * leer bzw. zeigt nur seinen Ersatzinhalt, und dessen Link (blob:, neuer Tab)
 * läuft ins Leere. Hier rendert pdf.js (liegt ohnehin im Projekt) die Seiten
 * als Bilder, der Reihe nach — die ersten Seiten sind sofort lesbar, der Rest
 * kommt nach. Dasselbe gilt im Handy-Browser (PdfPreviewModal bei schmalem
 * Schirm): Android zeigt ein eingebettetes PDF gar nicht, iOS nur Seite 1.
 *
 * Einbinden nur in diesen Zweigen: `istNativeApp() ? <NativePdfSeiten key={url} …/> : …`.
 * pdf.js wird erst beim Einhängen nachgeladen; der Desktop-Browser lädt davon nichts.
 *
 * Ein neuer Stand braucht einen neuen `key`: dann beginnt die Vorschau frisch
 * und gibt die Bilder des alten Stands frei.
 *
 * Füllt den nächsten positionierten Vorfahren (`absolute inset-0`).
 */
export const NativePdfSeiten: React.FC<NativePdfSeitenProps> = ({
    pdfUrl,
    onTeilen,
    teilenLaeuft = false,
    maxSeiten,
    nachladenSchritt,
    teilenText = 'PDF teilen / speichern',
}) => {
    const [seiten, setSeiten] = useState<string[]>([]);
    const [gesamt, setGesamt] = useState<number | null>(null);
    const [fehler, setFehler] = useState<string | null>(null);
    const [gross, setGross] = useState(false);
    // Aktuelle Seitengrenze des Laufs (wächst mit „Weitere Seiten anzeigen").
    const [grenze, setGrenze] = useState<number | null>(null);
    // Hebt die Grenze des laufenden Laufs an; gesetzt, sobald das Dokument geladen ist.
    const [weitereSeiten, setWeitereSeiten] = useState<((anzahl: number) => void) | null>(null);

    useEffect(() => {
        // Jeder Lauf beginnt mit leerem Stand: beim Einhängen ist er es ohnehin,
        // bei einem neuen `pdfUrl`/`maxSeiten` ohne neuen `key` setzt ihn das
        // Aufräumen des vorigen Laufs zurück (unten) — unmittelbar vor diesem.
        let aktiv = true;
        const bilder: string[] = [];
        // Ladeauftrag von pdf.js: hält den Worker. Zerstört wird ER (nicht nur
        // das Dokument), damit auch ein Aushängen WÄHREND getDocument den
        // Worker beendet — sonst liefe er verwaist weiter.
        let ladeauftrag: PDFDocumentLoadingTask | null = null;
        // Abbruch durch das Aufräumen („Loading aborted") ist kein Fehler.
        const fehlerMelden = (e: unknown) => {
            if (!aktiv) return;
            console.error('[NativePdfSeiten] Seitenvorschau in der App fehlgeschlagen', e);
            setFehler(e instanceof Error ? e.message : String(e));
        };
        (async () => {
            try {
                const [pdfjs, worker] = await Promise.all([
                    import('pdfjs-dist'),
                    import('pdfjs-dist/build/pdf.worker.min.mjs?url'),
                ]);
                // Worker aus dem Bundle wie in PdfCropper/stationery — nur setzen, falls noch leer.
                if (!pdfjs.GlobalWorkerOptions.workerSrc) pdfjs.GlobalWorkerOptions.workerSrc = worker.default as string;
                // blobFuerUrl statt fetch: data:-Adressen (Rechnungsvorschau) ohne
                // Netzweg, und eine eben freigegebene Objekt-URL findet die
                // Abfang-Schicht (nativeShims) noch kurz wieder.
                const daten = new Uint8Array(await (await blobFuerUrl(pdfUrl)).arrayBuffer());
                if (!aktiv) return;
                ladeauftrag = pdfjs.getDocument({ data: daten });
                const dok = await ladeauftrag.promise;
                // Inzwischen ausgehängt: das Aufräumen hat den Auftrag schon
                // zerstört; das Dokument trotzdem ausdrücklich freigeben.
                if (!aktiv) { zerstoere(dok); return; }
                // Seiten der Reihe nach bis zur Grenze. „Weitere Seiten" hebt sie
                // an und setzt DIESELBE Schleife fort: kein zweites Laden, und die
                // ersten Seiten bleiben stehen. Das Dokument lebt bis zum Aufräumen.
                let bis = seitenGrenze(dok.numPages, maxSeiten);
                let naechste = 1;
                let laeuft = false;
                const aufbereiten = async () => {
                    // Läuft die Schleife schon, liest sie die neue Grenze selbst.
                    if (laeuft) return;
                    laeuft = true;
                    try {
                        while (aktiv && naechste <= bis) {
                            const bild = await seiteAlsBild(dok, naechste);
                            if (!aktiv) { URL.revokeObjectURL(bild); return; }
                            bilder.push(bild);
                            naechste++;
                            setSeiten((vorher) => [...vorher, bild]);
                        }
                    } finally {
                        laeuft = false;
                    }
                };
                setGesamt(dok.numPages);
                setGrenze(bis);
                setWeitereSeiten(() => (anzahl: number) => {
                    if (!aktiv || !(anzahl >= 1)) return;
                    bis = Math.min(dok.numPages, bis + Math.floor(anzahl));
                    setGrenze(bis);
                    aufbereiten().catch(fehlerMelden);
                });
                await aufbereiten();
            } catch (e) {
                fehlerMelden(e);
            }
        })();
        return () => {
            aktiv = false;
            bilder.forEach((u) => URL.revokeObjectURL(u));
            // Beendet Worker und Dokument — auch mitten im Laden.
            zerstoere(ladeauftrag);
            // Frischer Stand für den nächsten Lauf (neuer pdfUrl/maxSeiten):
            // keine Bilder mit eben freigegebenen Adressen, kein alter Fehler.
            setSeiten([]);
            setGesamt(null);
            setFehler(null);
            setGrenze(null);
            setWeitereSeiten(null);
        };
    }, [pdfUrl, maxSeiten]);

    if (fehler && seiten.length === 0) {
        return (
            <div className="absolute inset-0 flex flex-col items-center text-center text-slate-600 px-6 justify-center">
                <FileText className="w-10 h-10 mb-4 text-slate-400" />
                <p className="font-bold mb-1">Seitenvorschau nicht verfügbar</p>
                <p className="text-xs text-slate-500 mb-4 max-w-sm break-words">
                    {onTeilen
                        ? <>Das PDF ist fertig. Über „{teilenText}" lässt es sich in einer PDF-App öffnen, ablegen oder versenden.</>
                        : 'Das PDF ist fertig, lässt sich hier aber nicht als Seiten darstellen.'}
                </p>
                {onTeilen && (
                    <Button variant="primary" onClick={onTeilen} disabled={teilenLaeuft} className="bg-blue-600 hover:bg-blue-700" icon={<Share2 className="w-4 h-4" />}>
                        {teilenText}
                    </Button>
                )}
            </div>
        );
    }

    const ziel = gesamt === null ? null : (grenze ?? seitenGrenze(gesamt, maxSeiten));
    const fertig = ziel !== null && seiten.length >= ziel;
    const gekuerzt = gesamt !== null && ziel !== null && ziel < gesamt;
    // Wie viele Seiten „Weitere Seiten anzeigen" dazuholt (0 = kein Knopf).
    const schritt = maxSeiten !== undefined && nachladenSchritt !== undefined && Number.isFinite(nachladenSchritt) && nachladenSchritt >= 1
        ? Math.floor(nachladenSchritt) : 0;
    const naechsteAnzahl = gesamt !== null && ziel !== null ? Math.min(schritt, gesamt - ziel) : 0;
    return (
        <div className="absolute inset-0 bg-slate-200">
            <div className="absolute inset-0 overflow-auto">
                <div className="p-2 space-y-2" style={{ width: gross ? '200%' : '100%' }}>
                    {seiten.map((src, i) => (
                        <img key={src} src={src} alt={`Seite ${i + 1}`} decoding="async" className="block w-full h-auto bg-white shadow" />
                    ))}
                    {!fertig && !fehler && (
                        <div className="flex items-center justify-center gap-2 py-6 text-slate-500 text-xs font-bold">
                            <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
                            {ziel === null ? 'Vorschau wird aufbereitet …' : `Seite ${Math.min(seiten.length + 1, ziel)} von ${ziel} wird aufbereitet …`}
                        </div>
                    )}
                    {fehler && (
                        <p className="text-center text-xs text-amber-700 py-4">Weitere Seiten ließen sich nicht darstellen — das PDF selbst ist vollständig.</p>
                    )}
                    {fertig && !fehler && gekuerzt && (
                        <div className="flex flex-col items-center gap-2 py-4 text-center">
                            <p className="text-xs text-slate-600">
                                Vorschau zeigt die ersten {ziel} von {gesamt} Seiten — das PDF selbst ist vollständig.
                            </p>
                            {weitereSeiten && naechsteAnzahl > 0 && (
                                <Button variant="secondary" onClick={() => weitereSeiten(naechsteAnzahl)} className="min-h-[44px] font-semibold">
                                    {naechsteAnzahl === 1 ? 'Nächste Seite anzeigen' : `Weitere ${naechsteAnzahl} Seiten anzeigen`}
                                </Button>
                            )}
                            {onTeilen && (
                                <Button variant="secondary" size="sm" onClick={onTeilen} disabled={teilenLaeuft} icon={<Share2 className="w-3.5 h-3.5" />}>
                                    {teilenText}
                                </Button>
                            )}
                        </div>
                    )}
                </div>
            </div>
            {/* Außerhalb des Scrollbereichs: bleibt beim Blättern an seinem Platz. */}
            <button
                type="button"
                onClick={() => setGross((g) => !g)}
                className="absolute bottom-4 right-4 z-10 bg-slate-900/80 text-white p-3 rounded-full shadow-lg"
                title={gross ? 'Verkleinern' : 'Vergrößern'}
                aria-label={gross ? 'Verkleinern' : 'Vergrößern'}
            >
                {gross ? <ZoomOut className="w-5 h-5" /> : <ZoomIn className="w-5 h-5" />}
            </button>
        </div>
    );
};
